import { useEffect, useState } from 'react';
import { FeatureGate } from '../../components/FeatureGate';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../../store/StoreContext';
import { ScreenHeader } from '../../components/ui/ScreenHeader';
import { FormSection } from '../../components/ui/FormSection';
import { TextField } from '../../components/ui/TextField';
import { Button } from '../../components/ui/Button';
import { AutoSaveStatus } from '../../components/ui/AutoSaveStatus';
import { useAutoSave } from '../../lib/useAutoSave';
import { AvatarView } from '../../components/ui/AvatarView';
import { AvatarImagePicker } from '../../components/ui/AvatarImagePicker';
import { Sheet } from '../../components/ui/Sheet';
import { AvatarSymbolPicker } from '../../components/ui/AvatarSymbolPicker';
import { Toast } from '../../components/ui/Toast';
import { PROFILE_SYMBOL_CHOICES } from '../../lib/icons';
import { requestAccountDeletion, exportAccountData } from '../../api/profileApi';

const AVATAR_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#0EA5E9'];

export function ProfileScreen() {
  const navigate = useNavigate();
  const {
    profile,
    setProfile,
    themeMode,
    language,
    syncSettings,
    logout,
    renameMyNickname,
    checkUserNicknameAvailable,
  } = useStore();
  const [draft, setDraft] = useState(profile);
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [error, setError] = useState('');

  // Rename is a dedicated migration (POST /profile/nickname - backend
  // internal/rename), not part of the auto-saved draft above (syncProfile
  // never even sends nickname - see profileApi.ts's own comment). Kept as
  // its own state, resynced whenever the real profile.nickname changes
  // (initial load, or right after a successful rename).
  const [nicknameDraft, setNicknameDraft] = useState(profile.nickname);
  const [nicknameStatus, setNicknameStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');
  const [renamingNickname, setRenamingNickname] = useState(false);
  useEffect(() => setNicknameDraft(profile.nickname), [profile.nickname]);
  useEffect(() => {
    const next = nicknameDraft.trim();
    if (!next || next === profile.nickname) {
      setNicknameStatus('idle');
      return;
    }
    setNicknameStatus('checking');
    const timer = setTimeout(async () => {
      const available = await checkUserNicknameAvailable(next);
      setNicknameStatus(available ? 'available' : 'taken');
    }, 400);
    return () => clearTimeout(timer);
  }, [nicknameDraft, profile.nickname, checkUserNicknameAvailable]);

  const confirmRenameNickname = async () => {
    setRenamingNickname(true);
    setError('');
    try {
      await renameMyNickname(nicknameDraft.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to change nickname.');
    } finally {
      setRenamingNickname(false);
    }
  };

  const auto = useAutoSave(draft, async (d) => {
    setProfile(d);
    await syncSettings(d, themeMode, language);
  });

  // The profile can arrive (or change on another device) after this screen
  // mounted; adopt it unless the user has unsaved edits, so a later save never
  // writes the stale first-render copy back over it.
  useEffect(() => {
    if (!auto.isDirty) setDraft(profile);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  useEffect(() => {
    // Local state is already applied in the saveFn, so the edit isn't lost -
    // only the cross-device sync failed.
    if (auto.error) setError('Saved locally, but could not sync to the server.');
  }, [auto.error]);
  const [toast, setToast] = useState('');
  const [exporting, setExporting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deletionNotice, setDeletionNotice] = useState('');

  const exportData = async () => {
    setExporting(true);
    try {
      await exportAccountData();
      setToast('Your data export has been downloaded.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not export your data.');
    } finally {
      setExporting(false);
    }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      const result = await requestAccountDeletion();
      const when = result.purgeAfter
        ? new Date(result.purgeAfter).toLocaleDateString()
        : `in ${result.graceDays} days`;
      setDeletionNotice(
        `Your account is deactivated and scheduled for permanent deletion on ${when}. Sign in again before then to cancel.`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not schedule account deletion.');
      setDeleting(false);
    }
  };

  const finishDeletion = () => {
    logout();
    navigate('/start');
  };

  return (
    <div className="flex flex-col gap-5" onBlur={auto.flush}>
      <ScreenHeader title="Profile" action={<AutoSaveStatus status={auto.status} onRetry={auto.retry} />} />

      <FormSection title="Avatar">
        <div className="flex items-center gap-4">
          <div className="relative">
            <AvatarView symbol={draft.avatarSymbol} colorHex="#3B82F6" imageKey={draft.avatarImageKey} size={72} zoomable />
            <div className="absolute -bottom-1 -right-1">
              <AvatarImagePicker
                size={72}
                onUploaded={(key) => setDraft((d) => ({ ...d, avatarImageKey: key }))}
                onError={setError}
              />
            </div>
          </div>
          <Button variant="bordered" onClick={() => setShowAvatarPicker(true)}>
            Choose icon
          </Button>
        </div>
      </FormSection>

      <FormSection title="Profile">
        <TextField label="Name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
        <div className="flex flex-col gap-1.5">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <TextField
                label="Nickname"
                value={nicknameDraft}
                onChange={(e) => setNicknameDraft(e.target.value)}
                error={nicknameStatus === 'taken' ? 'That nickname is already taken.' : undefined}
              />
            </div>
            {nicknameDraft.trim() && nicknameDraft.trim() !== profile.nickname && (
              <Button
                variant="bordered"
                onClick={confirmRenameNickname}
                disabled={nicknameStatus !== 'available' || renamingNickname}
                loading={renamingNickname}
              >
                Rename
              </Button>
            )}
          </div>
          {nicknameStatus === 'checking' && (
            <span className="text-xs text-black/40 dark:text-white/40">Checking availability…</span>
          )}
          {nicknameStatus === 'available' && (
            <span className="text-xs text-emerald-600 dark:text-emerald-400">Available — click Rename to confirm.</span>
          )}
        </div>
        <TextField label="Phone" value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} />
        <TextField
          label="Website"
          value={draft.website}
          onChange={(e) => setDraft({ ...draft, website: e.target.value })}
        />
        <TextField
          label="Instagram"
          value={draft.instagram}
          onChange={(e) => setDraft({ ...draft, instagram: e.target.value })}
        />
        <TextField
          label="WhatsApp"
          value={draft.whatsapp}
          onChange={(e) => setDraft({ ...draft, whatsapp: e.target.value })}
        />
        <TextField
          label="Messenger"
          value={draft.messenger}
          onChange={(e) => setDraft({ ...draft, messenger: e.target.value })}
        />
        <TextField
          label="Telegram"
          value={draft.telegram}
          onChange={(e) => setDraft({ ...draft, telegram: e.target.value })}
        />
        <TextField
          label="LinkedIn"
          value={draft.linkedin}
          onChange={(e) => setDraft({ ...draft, linkedin: e.target.value })}
        />
      </FormSection>

      <FeatureGate feature="account.data_export" fallback={null}>
      <FormSection title="Your data">
        <p className="text-xs text-black/50 dark:text-white/50">
          Download everything we hold for your account — your profile, the channels you belong to, and every
          signal you've sent — as a JSON file.
        </p>
        <Button variant="bordered" full loading={exporting} disabled={exporting} onClick={exportData}>
          Download my data
        </Button>
      </FormSection>
      </FeatureGate>

      <FormSection title="Delete account">
        <p className="text-xs text-black/50 dark:text-white/50">
          Your account is deactivated right away and permanently deleted after a 7-day grace period, along with
          the channels, schemas, and signals you own. Sign in again before then to cancel.
        </p>
        <Button full variant="destructive" onClick={() => { setDeleteConfirmText(''); setShowDeleteConfirm(true); }}>
          Delete account
        </Button>
      </FormSection>

      <Sheet
        open={showDeleteConfirm}
        onClose={() => {
          if (deleting) return;
          if (deletionNotice) finishDeletion();
          else setShowDeleteConfirm(false);
        }}
        title={deletionNotice ? 'Account scheduled for deletion' : 'Delete account?'}
      >
        {deletionNotice ? (
          <div className="flex flex-col gap-4">
            <p className="text-sm text-black/70 dark:text-white/70">{deletionNotice}</p>
            <Button variant="primary" full onClick={finishDeletion}>
              Done
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <p className="text-sm text-black/70 dark:text-white/70">
              This deactivates your account now and schedules it — and everything you own — for permanent
              deletion in 7 days. Type <span className="font-semibold">DELETE</span> to confirm.
            </p>
            <TextField
              label="Confirm"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="DELETE"
            />
            <div className="flex gap-2">
              <Button variant="bordered" full disabled={deleting} onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                full
                loading={deleting}
                disabled={deleting || deleteConfirmText.trim() !== 'DELETE'}
                onClick={confirmDelete}
              >
                Delete account
              </Button>
            </div>
          </div>
        )}
      </Sheet>

      <Sheet open={showAvatarPicker} onClose={() => setShowAvatarPicker(false)} title="Choose avatar">
        <AvatarSymbolPicker
          symbols={PROFILE_SYMBOL_CHOICES}
          colors={AVATAR_COLORS}
          onPick={(symbol) => {
            setDraft((d) => ({ ...d, avatarSymbol: symbol }));
            setShowAvatarPicker(false);
          }}
        />
      </Sheet>

      {error && <Toast message={error} onDismiss={() => setError('')} />}
      {toast && <Toast message={toast} onDismiss={() => setToast('')} />}
    </div>
  );
}
